
import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Clock } from 'lucide-react';

interface DailyCallManagerProps {
  isCallActive: boolean;
  expiryTimestamp?: number; // Unix timestamp when call expires
  onCallEnd: () => void;
}

export const DailyCallManager = ({ isCallActive, expiryTimestamp, onCallEnd }: DailyCallManagerProps) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const warningTimerRef = useRef<NodeJS.Timeout>();
  const endTimerRef = useRef<NodeJS.Timeout>();
  const countdownRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isCallActive && expiryTimestamp) {
      const now = Math.floor(Date.now() / 1000);
      const timeLeft = expiryTimestamp - now;
      
      if (timeLeft <= 0) {
        // Call already expired
        endCall();
        return;
      }
      
      // Clear any existing timers
      clearAllTimers();
      
      const warningSeconds = Math.max(0, timeLeft - (10 * 60)); // 10 minutes before end
      
      // Set warning timer
      if (warningSeconds > 0) {
        warningTimerRef.current = setTimeout(() => {
          setShowWarning(true);
          startCountdown();
        }, warningSeconds * 1000);
      } else {
        // Less than 10 minutes remaining, show warning immediately
        setShowWarning(true);
        startCountdown();
      }
      
      // Set end timer (Daily.co will also eject at this time)
      endTimerRef.current = setTimeout(() => {
        endCall();
      }, timeLeft * 1000);
    } else {
      clearAllTimers();
      setShowWarning(false);
    }
    
    return () => clearAllTimers();
  }, [isCallActive, expiryTimestamp]);

  const startCountdown = () => {
    if (!expiryTimestamp) return;
    
    countdownRef.current = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      const timeLeft = Math.max(0, expiryTimestamp - now);
      setTimeRemaining(timeLeft);
      
      if (timeLeft <= 0) {
        clearInterval(countdownRef.current);
        endCall();
      }
    }, 1000);
    
    // Set initial countdown time
    const now = Math.floor(Date.now() / 1000);
    const timeLeft = Math.max(0, expiryTimestamp - now);
    setTimeRemaining(timeLeft);
  };

  const clearAllTimers = () => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = undefined;
    }
    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = undefined;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = undefined;
    }
    setShowWarning(false);
    setTimeRemaining(0);
  };

  const endCall = () => {
    try {
      // Since we can't directly control the Daily.co call from here,
      // we'll close the call window and notify the parent to handle cleanup
      console.log('Call time expired - notifying to end call');
    } catch (error) {
      console.error('Error ending call:', error);
    }
    
    clearAllTimers();
    onCallEnd();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <AlertTriangle className="h-5 w-5" />
            Call Time Warning
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-center">
            <Clock className="h-12 w-12 text-orange-500 mx-auto mb-2" />
            <p className="text-lg font-semibold">
              Time remaining: {formatTime(timeRemaining)}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              This call will automatically end when the scheduled time is up.
            </p>
          </div>
          
          <div className="flex justify-center">
            <Button 
              onClick={() => setShowWarning(false)}
              className="bg-emu-blue hover:bg-emu-blue/90"
            >
              Continue Call
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
